import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import BrandLogo from './brand/BrandLogo';
import authService from '../services/auth';
import { PRODUCT_NAME } from '../utils/brandCopy';
import { useThemeMode } from '../theme/ThemeModeProvider';
import ColorwayPicker from './public/ColorwayPicker';
import type { ThemeModePreference } from '../theme/tokens';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
}));

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  borderBottom: active
    ? `2px solid ${theme.palette.brand.yellow}`
    : '2px solid transparent',
  borderRadius: 0,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    borderBottom: `2px solid ${theme.palette.brand.yellow}`,
    backgroundColor: 'transparent',
  },
}));

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { preference, setPreference } = useThemeMode();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toolsEl, setToolsEl] = React.useState<null | HTMLElement>(null);
  const [opsRole, setOpsRole] = React.useState(false);

  React.useEffect(() => {
    if (!authService.getToken() || typeof authService.me !== 'function') return undefined;
    let cancelled = false;
    authService
      .me()
      .then((user) => {
        if (!cancelled) setOpsRole(user.role === 'admin' || user.role === 'developer');
      })
      .catch(() => {
        if (!cancelled) setOpsRole(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onDashboard =
    location.pathname === '/dashboard' || location.hash === '#/dashboard';
  const onAnalyze =
    location.pathname === '/deals/new' || location.hash.includes('/deals/new');
  const onTools = location.pathname.startsWith('/tools') || location.hash.includes('/tools');
  const onLearn = location.pathname === '/learn' || location.pathname.startsWith('/learn/') || location.hash.includes('/learn');
  const onProfile = location.pathname.includes('/profile') || location.hash.includes('/profile');

  const go = (path: string) => {
    setAnchorEl(null);
    setToolsEl(null);
    navigate(path);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  return (
    <StyledAppBar position="static">
      <Toolbar sx={{ gap: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 2 }}
          onClick={() => navigate('/dashboard')}
        >
          <BrandLogo variant="mark" height={36} />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 800, color: 'text.primary' }}>
            {PRODUCT_NAME}
          </Typography>
        </Box>

        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.5, flexGrow: 1, alignItems: 'center' }}>
          <NavButton active={onDashboard} onClick={() => navigate('/dashboard')}>
            Portfolio
          </NavButton>
          <NavButton active={onAnalyze} onClick={() => navigate('/deals/new')}>
            Analyze
          </NavButton>
          <NavButton active={onTools} onClick={(e) => setToolsEl(e.currentTarget)} aria-haspopup="true">
            Tools
          </NavButton>
          <NavButton active={onLearn} onClick={() => navigate('/learn')}>
            Learn
          </NavButton>
          <NavButton active={onProfile} onClick={() => navigate('/profile')}>
            Profile
          </NavButton>
          {opsRole && (
            <NavButton active={location.pathname === '/admin'} onClick={() => navigate('/admin')}>
              Admin
            </NavButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Tooltip title="Theme">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={preference}
              onChange={(_, v: ThemeModePreference | null) => {
                if (v) setPreference(v);
              }}
              aria-label="Color theme"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <ToggleButton value="light" aria-label="Light">
                <LightModeIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Dark">
                <DarkModeIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="system" aria-label="System">
                <SettingsBrightnessIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <ColorwayPicker />
          </Box>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleLogout}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, textTransform: 'none' }}
          >
            Logout
          </Button>
          <IconButton
            size="large"
            aria-label="account menu"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            color="inherit"
            sx={{ display: { sm: 'none' } }}
          >
            <AccountCircle />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => go('/dashboard')}>Portfolio</MenuItem>
          <MenuItem onClick={() => go('/deals/new')}>Analyze</MenuItem>
          <MenuItem onClick={() => go('/tools')}>Tools</MenuItem>
          <MenuItem onClick={() => go('/tools/analyzer')}>Deal Analyzer</MenuItem>
          <MenuItem onClick={() => go('/tools/comps')}>Comp Finder</MenuItem>
          <MenuItem onClick={() => go('/tools/risk')}>Risk & Stress Test</MenuItem>
          <MenuItem onClick={() => go('/tools/review')}>AI Deal Review</MenuItem>
          <MenuItem onClick={() => go('/learn')}>Learn</MenuItem>
          <MenuItem onClick={() => go('/profile')}>Profile</MenuItem>
          {opsRole && <MenuItem onClick={() => go('/admin')}>Admin</MenuItem>}
          <MenuItem
            onClick={() => {
              setPreference('light');
              setAnchorEl(null);
            }}
          >
            Light theme
          </MenuItem>
          <MenuItem
            onClick={() => {
              setPreference('dark');
              setAnchorEl(null);
            }}
          >
            Dark theme
          </MenuItem>
          <MenuItem
            onClick={() => {
              setPreference('system');
              setAnchorEl(null);
            }}
          >
            System theme
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              handleLogout();
            }}
          >
            Logout
          </MenuItem>
        </Menu>
        <Menu anchorEl={toolsEl} open={Boolean(toolsEl)} onClose={() => setToolsEl(null)}>
          <MenuItem onClick={() => go('/tools')}>Tools hub</MenuItem>
          <MenuItem onClick={() => go('/tools/analyzer')}>Deal Analyzer</MenuItem>
          <MenuItem onClick={() => go('/tools/comps')}>Comp Finder</MenuItem>
          <MenuItem onClick={() => go('/tools/risk')}>Risk & Stress Test</MenuItem>
          <MenuItem onClick={() => go('/tools/review')}>AI Deal Review</MenuItem>
        </Menu>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Navbar;
